import type {
	SearchMemoryEntity,
	SearchMemoryResult,
} from "@/lib/api/search-memory";
import { buildEntityUrl } from "@/lib/url";

export type MemoryHitKind = "entity" | "event" | "connection";

export type MemoryTypeInfo = {
	name: string;
	icon?: string | null;
};

export type MemoryHit = {
	id: string;
	kind: MemoryHitKind;
	kindLabel: string;
	typeLabel: string;
	name: string;
	href: string;
	icon: string | null;
};

const MAX_HITS = 12;

function workspaceSlug(
	row: { workspace_slug?: string | null; organization_slug?: string | null },
	fallback: string,
): string {
	return row.workspace_slug || row.organization_slug || fallback;
}

/**
 * A type's own display name. Until one is known, the slug as stored — never a
 * name guessed by capitalising it. Connector labels below follow the same rule.
 */
function typeLabelFor(
	slug: string,
	types: Map<string, MemoryTypeInfo>,
): string {
	return types.get(slug)?.name?.trim() || slug;
}

function entityHref(entity: SearchMemoryEntity, owner: string): string {
	const o = workspaceSlug(entity, owner);
	const segments = [];
	if (entity.parent_entity_type && entity.parent_slug) {
		segments.push({
			entity_type: entity.parent_entity_type,
			slug: entity.parent_slug,
		});
	}
	segments.push({ entity_type: entity.type, slug: entity.slug });
	return buildEntityUrl(o, segments);
}

/**
 * Flatten a `search_memory` payload into command-palette rows:
 * `Entity → Person → Burak`, `Connection → Gmail → Work`, `Event → Slack → …`.
 * `connectorNames` maps a connector key to its catalog display name.
 */
export function flattenSearchMemoryHits(
	result: SearchMemoryResult | undefined,
	owner: string,
	types: Map<string, MemoryTypeInfo> = new Map(),
	connectorNames: ReadonlyMap<string, string> = new Map(),
): MemoryHit[] {
	if (!result || !owner) return [];
	const hits: MemoryHit[] = [];
	const seenEntities = new Set<number>();

	const pushEntity = (entity: SearchMemoryEntity) => {
		if (seenEntities.has(entity.id)) return;
		seenEntities.add(entity.id);
		hits.push({
			id: `entity-${entity.id}`,
			kind: "entity",
			kindLabel: "Entity",
			typeLabel: typeLabelFor(entity.type, types),
			name: entity.name,
			href: entityHref(entity, owner),
			icon: types.get(entity.type)?.icon ?? null,
		});
	};

	if (result.entity) pushEntity(result.entity);
	for (const match of result.matches ?? []) pushEntity(match);

	for (const connection of result.connections ?? []) {
		const connectorKey =
			typeof connection.connector_key === "string"
				? connection.connector_key
				: "";
		hits.push({
			id: `connection-${connection.connection_id}`,
			kind: "connection",
			kindLabel: "Connection",
			typeLabel:
				(connectorKey && connectorNames.get(connectorKey)) ||
				connectorKey ||
				"Connection",
			name: connection.display_name || connectorKey || "Connection",
			href: `/${workspaceSlug(connection, owner)}/connectors/${connectorKey}/${connection.connection_id}`,
			icon: null,
		});
	}

	for (const item of result.content ?? []) {
		const rawText =
			typeof item.text_content === "string" ? item.text_content.trim() : "";
		const title =
			(typeof item.title === "string" && item.title.trim().length > 0
				? item.title.trim()
				: null) ??
			(rawText.slice(0, 80) || "Untitled");
		hits.push({
			id: `event-${item.id}`,
			kind: "event",
			kindLabel: "Event",
			typeLabel:
				(item.platform && connectorNames.get(item.platform)) ||
				item.platform ||
				"Memory",
			name: title,
			href: `/${workspaceSlug(item, owner)}/events?content_ids=${item.id}`,
			icon: null,
		});
	}

	return hits.slice(0, MAX_HITS);
}
